<template>
  <v-card id="card-cr">
    <v-card-text>
      <v-row class="justify-center">
        <v-col cols="12" class="text-left info-box">
          <h2 aria-live="assertive" class="text-center text-h2">
            {{ $t('L_start_new_policy') }}
          </h2>
          <h4 class="mt-3 text-h4 font-weight-regular">
            {{ $t('L_you_can_lock_in') }}
          </h4>
          <h4 class="mt-3 text-h4 font-weight-regular">
            {{ custServNum }}
          </h4>
        </v-col>
        <v-col cols="12">
          <v-form
            id="registration-form"
            ref="form"
            v-model="validForm"
            @update:modelValue="onFormValidityChanges"
          >
            <OnexFirstName
              v-model="firstName"
              input-id="first_name"
              :label="$t('L_firstname')"
              :auto-capitalize="true"
              required
            />
            <OnexMiddleName
              input-id="middle_name"
              v-model="middleName"
              :label="$t('L_middle_name')"
            />
            <OnexLastName
              input-id="last_name"
              v-model="lastName"
              :label="$t('L_last_name')"
              required
            />
            <OnexDatePicker
              data-comp-id="birth_date"
              type="dob"
              :label="$t('L_birth_date')"
              :date-format="dobFormatLanguage"
              :max="maxDateOfBirth"
              :min="minDateOfBirth"
              :out-of-range-message="outOfRangeMessageText"
              required
              disabled
              v-model="birthDate"
              :key="locale"
            />
            <OnexSsn
              v-if="isCanadianPolicy === 'ca'"
              data-comp-id="recruit_ssn"
              :format="isCanadianPolicy !== 'ca' ? SSNFormat.SSN : SSNFormat.SIN"
              allow-sin-business
              allow-sin-itn
              :lastFour="ssn"
              v-model="ssn"
              :label="getSSNLabel"
            >
            </OnexSsn>
            <OnexSsn
              v-if="isCanadianPolicy !== 'ca'"
              data-comp-id="recruit_ssn"
              :format="isCanadianPolicy !== 'ca' ? SSNFormat.SSN : SSNFormat.SIN"
              allow-sin-business
              allow-sin-itn
              :lastFour="ssn"
              required
              v-model="ssn"
              :label="getSSNLabel"
            >
            </OnexSsn>
            <OnexRadioGroup
              id="gender"
              v-model="gender"
              :mandatory="true"
              :rules="[(v: any) => !!v]"
              :label="$t('L_gender')"
              :required-msg-override="'Gender is required'"
              :required="true"
            >
              <OnexRadio
                class="mb-4"
                v-for="(option, index) in genders"
                :id="option.id || 'radio_input_' + index"
                :key="option.label"
                :label="$t(option.label)"
                :value="option.value"
              >
                <template #label>
                  <span :class="$vuetify.display.smAndUp ? 'font-size--16' : 'font-size--14'">
                    {{ $t(`${option.label}`) }}
                  </span>
                </template>
              </OnexRadio>
            </OnexRadioGroup>
            <div class="d-flex justify-center">
              <OnexButton
                color="primary"
                width="200"
                @click="next"
                :disabled="!validForm || !(ssn.length > 8 && birthDate && gender)"
              >
                {{ $t('L_next') }}
              </OnexButton>
            </div>
          </v-form>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { mapActions, mapState } from 'pinia';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useChildRiderStore } from '../../store/childrider.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import {
  OnexButton,
  OnexRadioGroup,
  OnexRadio,
  OnexFirstName,
  OnexMiddleName,
  OnexLastName,
  OnexSsn,
  OnexDatePicker,
  DateFormats,
  SSNFormat,
} from '@primerica/onex-ui-lib';
import { format, isValid, parseISO } from 'date-fns';

const componentOptions: ComponentOptions = {
  name: 'CRCStepAPersonalInfo',
  components: {
    OnexButton,
    OnexRadioGroup,
    OnexRadio,
    OnexFirstName,
    OnexMiddleName,
    OnexLastName,
    OnexDatePicker,
    OnexSsn,
  },
  mounted() {
    window.scrollTo(0, 0);
    const parsedBirthDate = this.NPDOB ? parseISO(this.NPDOB) : null;
    this.birthDate = parsedBirthDate && isValid(parsedBirthDate) ? parsedBirthDate : null;
    if (this.NPGender) {
      this.gender = this.NPGender;
    }
    if (this.NPFirstName) {
      this.firstName = this.NPFirstName;
    }
    if (this.NPLastName) {
      this.lastName = this.NPLastName;
    }
    if (this.NPMiddleName) {
      this.middleName = this.NPMiddleName;
    }
    if (this.NPSSN) {
      this.ssn = this.NPSSN;
    }
  },
  onErrorCaptured(err: unknown, _: unknown, info: string) {
    try {
      this.$dynatrace.reportCustomError(
        'PC2_CRCStepAPersonalInfo',
        `[CRCStepAPersonalInfoError] ${info}: ${JSON.stringify(err)}`
      );
    } catch (_error) {
      /* empty */
    }
    return false; // Prevent the error from propagating further
  },
  data: () => ({
    validForm: false,
    birthDate: null as Date | null,
    gender: '',
    firstName: '',
    lastName: '',
    middleName: '',
    ssn: '',
  }),
  computed: {
    SSNFormat() {
      return SSNFormat;
    },
    ...mapState(useGlobalStore, {
      dateFormat: 'getDateFormat',
      locale: 'getLocale',
    }),
    ...mapState(usePolicyStore, {
      currentPolicy: 'currentPolicy',
      issueDateForCurrentPolicy: 'issueDateForCurrentPolicy',
    }),
    ...mapState(useCustomerStore, {
      getCustServNumWDash: 'getCustServNumWDash',
    }),
    ...mapState(useChildRiderStore, {
      NPFirstName: 'NPFirstName',
      NPLastName: 'NPLastName',
      NPMiddleName: 'NPMiddleName',
      NPGender: 'NPGender',
      NPDOB: 'NPDOB',
      NPSSN: 'NPSSN',
    }),
    outOfRangeMessageText() {
      if (!this.birthDate) {
        return;
      }
      const dobDate = new Date(this.birthDate);
      if (dobDate >= this.maxDateOfBirth) {
        return this.$t('L_child_too_young');
      } else if (dobDate <= this.dobOfMaxAge) {
        if (this.currentPolicy.nextGenPolicy) {
          return this.$t('L_child_too_old_next_gen');
        } else {
          return this.$t('L_child_too_old');
        }
      } else if (
        this.issueDateForCurrentPolicy &&
        new Date(this.issueDateForCurrentPolicy) >= dobDate
      ) {
        return this.$t('L_born_after_issued');
      }
    },
    dobOfMaxAge() {
      const maxAge = this.currentPolicy?.nextGenPolicy ? 17.5 : 24.5;
      const today = new Date();
      const dobOfMaxAge = new Date(today);
      dobOfMaxAge.setFullYear(today.getFullYear() - maxAge);
      return dobOfMaxAge;
    },
    minDateOfBirth() {
      if (
        !this.issueDateForCurrentPolicy ||
        new Date(this.issueDateForCurrentPolicy) < this.dobOfMaxAge
      ) {
        // return dob of max age when no issue data, or issue date is earlier.
        return this.dobOfMaxAge;
      }

      return new Date(this.issueDateForCurrentPolicy);
    },
    maxDateOfBirth() {
      const today = new Date();
      today.setDate(today.getDate() - 15);
      return today;
    },
    formattedBirthDate() {
      if (!this.birthDate) {
        return '';
      }
      // Keep the exact calendar day selected in the date picker.
      // Re-parsing through `new Date('yyyy-MM-dd')` can shift by timezone.
      return format(this.birthDate, 'yyyy-MM-dd');
    },
    custServNum() {
      if (this.locale?.length > 0) {
        const withDash = this.getCustServNumWDash(this.locale);
        return this.$t('L_use_the_form_below', {
          dashedPhoneNum: withDash,
        });
      }
      return '';
    },
    isFrenchLocale() {
      return this.locale.indexOf('fr') !== -1;
    },
    genders() {
      return [
        {
          label: 'L_male',
          value: 'M',
        },
        {
          label: 'L_female',
          value: 'F',
        },
      ];
    },
    isUsPolicy() {
      return !!(this.currentPolicy?.isUSPolicy || this.currentPolicy?.isNewYorkPolicy);
    },
    isCanadianPolicy() {
      return !!this.currentPolicy?.isCanadaPolicy;
    },
    isQuebecPolicy() {
      return this.currentPolicy?.isCanadaQuebecPolicy;
    },
    getSSNLabel() {
      return this.isCanadianPolicy ? this.$t('L_sin') : this.$t('L_ssn');
    },
    dobFormatLanguage() {
      return this.isCanadianPolicy ? DateFormats.DDMMYYYY : DateFormats.MMDDYYYY;
    },
  },
  methods: {
    ...mapActions(useChildRiderStore, {
      crcNext: 'crcNext',
      setNPFirstName: 'setNPFirstName',
      setNPMiddleName: 'setNPMiddleName',
      setNPLastName: 'setNPLastName',
      setNPDOB: 'setNPDOB',
      setNPGender: 'setNPGender',
      setNPSSN: 'setNPSSN',
    }),
    onFormValidityChanges(e: boolean) {
      this.validForm = e;
    },
    next() {
      this.setNPFirstName(this.firstName);
      this.setNPMiddleName(this.middleName);
      this.setNPLastName(this.lastName);
      this.setNPDOB(this.formattedBirthDate);
      this.setNPGender(this.gender);
      this.setNPSSN(this.ssn);
      this.crcNext();
    },
  },
};

export default defineComponent(componentOptions);
</script>
<style lang="scss" scoped>
#card-cr {
  margin-bottom: 5rem;
}
#registration-form {
  max-width: 560px;
  margin: 0 auto;
}
.info-box {
  max-width: 760px;
  margin: 0 auto;
  text-align: left;
}
</style>
